import { useEffect, useState } from "react";
import { ListMusic, Plus, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import EmptyStateShell from "./EmptyStateShell";

const STARTER_TEMPLATES = [
  { name: "BFCM Prep 🛍️", description: "Episodes to gear up for Black Friday & Cyber Monday." },
  { name: "New Hire Onboarding 👋", description: "The must-listen primer for new ecomm team members." },
  { name: "Retention Deep Dives 🔁", description: "LTV, subscriptions, win-back — all in one place." },
];

/**
 * Empty state for the Playlists tab.
 *
 * Provides:
 *  - A primary CTA opening the create flow (parent-controlled).
 *  - 2-3 starter templates that prefill the create form on click.
 *  - Cross-promo if the user has saved episodes ("Want to organize them?").
 */
export default function EmptyPlaylists({
  onStartCreate,
}: {
  onStartCreate: (preset?: { title: string; description?: string }) => void;
}) {
  const { user } = useAuth();
  const [savedCount, setSavedCount] = useState<number>(0);

  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from("saved_episodes")
      .select("id", { count: "exact", head: true })
      .eq("user_id", user.id)
      .then(({ count }) => setSavedCount(count ?? 0));
  }, [user?.id]);

  return (
    <EmptyStateShell
      icon={ListMusic}
      emoji="🎵"
      headline="Mixtapes for ecomm wisdom."
      blurb="Group episodes by theme — BFCM prep, new hire onboarding, deep-dives on retention. Share them publicly to flex your taste, or keep them private as a personal study list."
      rotation={-0.8}
    >
      <button
        onClick={() => onStartCreate()}
        className="btn-comic btn-comic-shift w-full gap-2 text-xs sm:text-sm px-4 py-2.5 sm:py-3 mb-3 sm:mb-4"
      >
        <Plus className="w-4 h-4 relative z-10" />
        <span className="relative z-10">Create your first playlist</span>
      </button>

      {savedCount > 0 && (
        <div className="retro-border bg-retro-amber/15 px-2.5 py-2 mb-3 sm:mb-4 flex items-start gap-2">
          <Sparkles className="w-3.5 h-3.5 text-primary flex-shrink-0 mt-0.5" />
          <p className="font-mono text-[10px] sm:text-[11px] text-foreground leading-snug">
            You have <strong>{savedCount}</strong> saved episode{savedCount !== 1 ? "s" : ""} — perfect to organize!
          </p>
        </div>
      )}

      <p className="font-mono text-[10px] sm:text-[11px] uppercase tracking-wider text-muted-foreground font-bold mb-2">
        ✨ Or start from a template
      </p>
      <ul className="space-y-1.5">
        {STARTER_TEMPLATES.map((tpl) => (
          <li key={tpl.name}>
            <button
              onClick={() => onStartCreate({ title: tpl.name, description: tpl.description })}
              className="w-full text-left font-mono text-[11px] sm:text-xs px-3 py-2 retro-border bg-background hover:bg-retro-amber/25 active:translate-y-px transition-colors"
            >
              <span className="font-bold">{tpl.name}</span>
              <span className="block text-[9px] sm:text-[10px] text-muted-foreground mt-0.5 leading-snug">{tpl.description}</span>
            </button>
          </li>
        ))}
      </ul>
    </EmptyStateShell>
  );
}
